<script setup lang="ts">
import { computed, useAttrs } from 'vue';

type SkeletonType = 'text' | 'rect' | 'circle';
type SkeletonAnimation = 'pulse' | 'wave' | 'none';

interface Props {
  width?: string | number;
  height?: string | number;
  type?: SkeletonType;
  animation?: SkeletonAnimation;
  square?: boolean;
}

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<Props>(), {
  width: undefined,
  height: undefined,
  type: 'text',
  animation: 'wave',
  square: false,
});

const attrs = useAttrs();

const sizeStyle = computed(() => {
  const width = props.width !== undefined ? String(props.width) : undefined;
  const height = props.height !== undefined ? String(props.height) : undefined;
  const resolvedWidth = width ? (/\d$/.test(width) ? `${width}px` : width) : undefined;
  const resolvedHeight = height ? (/\d$/.test(height) ? `${height}px` : height) : undefined;
  return {
    width: resolvedWidth,
    height: resolvedHeight,
  };
});

const classes = computed(() => [
  'ui-skeleton',
  `ui-skeleton--${props.type}`,
  props.animation !== 'none' ? `ui-skeleton--${props.animation}` : null,
  props.square ? 'ui-skeleton--square' : null,
  attrs.class,
]);
</script>

<template>
  <div :class="classes" :style="{ ...sizeStyle, ...(attrs.style as object) }" />
</template>

<style scoped lang="scss">
.ui-skeleton {
  position: relative;
  overflow: hidden;
  background: linear-gradient(90deg, $gray-100 0%, rgb(236 238 241 / 0%) 76.88%, rgb(239 242 247 / 45.4%) 99.4%);
  border-radius: 4px;
  height: 24px;

  &--text {
    border-radius: 4px;
    height: 24px;
  }

  &--rect {
    border-radius: 8px;
  }

  &--circle {
    border-radius: 50%;
    width: 40px;
    height: 40px;
  }

  &--square {
    border-radius: 0;
  }

  &--pulse {
    animation: ui-skeleton-pulse 1.3s ease-in-out infinite;
  }

  &--wave::after {
    content: '';
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(90deg, transparent, rgb(255 255 255 / 35%), transparent);
    animation: ui-skeleton-wave 1.4s ease-in-out infinite;
  }
}

@keyframes ui-skeleton-pulse {
  0%,
  100% {
    opacity: 0.55;
  }
  50% {
    opacity: 1;
  }
}

@keyframes ui-skeleton-wave {
  100% {
    transform: translateX(100%);
  }
}
</style>
